import usePagedCollection from '../../hooks/usePagedCollection';
import InfiniteScrollSentinel from '../common/InfiniteScrollSentinel';
import { useState, useCallback, useEffect } from 'react';
import { useCommunityPageQuery } from '../../redux/apis/endpoints/sharedClosetApi';
import { getThumbnailUrl } from '../../utils/imageUrl';

import './SharedClosetBrowser.css';

/**
 * Get the display image URL for a community item.
 * Uses the same priority as ClothingCardsGrid: generatedProductPhoto > removedBgImage > original.
 * @param {Object} item - Community item (a shared closet row or a designer piece)
 * @returns {string} Image URL with thumbnail transform applied
 */
const getItemImageUrl = (item) => {
  if (item.generatedProductPhoto?.url) return getThumbnailUrl(item.generatedProductPhoto.url);
  if (item.removedBgImage?.url) return getThumbnailUrl(item.removedBgImage.url);
  const originalUrl = item.image?.url || item.image;
  return getThumbnailUrl(originalUrl);
};

/**
 * Loading skeleton card for the community grid.
 */
const SkeletonCard = () => (
  <div className="shared-item-skeleton">
    <div className="shared-item-skeleton-image" />
    <div className="shared-item-skeleton-text">
      <div className="shared-item-skeleton-line" />
    </div>
  </div>
);

/**
 * SharedClosetBrowser - the community feed: designer pieces from SHEYOU mixed
 * with looks other members have shared, in a seeded random order.
 *
 * @param {Object} props
 * @param {Function} props.onItemClick - Callback when an item is clicked (for try-on)
 * @param {Function} props.onBack - Callback to return to the My Closet view
 */
const SharedClosetBrowser = ({ onItemClick, onBack }) => {
  // Held for the session so the order stays put while paging through it.
  const [seed] = useState(() => Math.random().toString(36).slice(2));
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');

  // Debounced so typing does not fire a request per keystroke; the paging hook
  // keys its cache on the args, so a new query resets to the first page.
  useEffect(() => {
    const id = setTimeout(() => setQuery(searchInput.trim()), 250);
    return () => clearTimeout(id);
  }, [searchInput]);

  const { items, isLoading, isFetching, isError, error, hasMore, loadMore, refetch } =
    usePagedCollection(useCommunityPageQuery, { _sort: 'random', seed, ...(query ? { q: query } : {}) });

  const ownItems = items.filter(item => item.isOwn);
  const communityItems = items.filter(item => !item.isOwn);

  const handleItemClick = useCallback((item) => {
    if (onItemClick) {
      onItemClick(item);
    }
  }, [onItemClick]);

  const searchBar = (
    <div className="shared-closet-search">
      <svg className="shared-closet-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <input
        type="search"
        className="shared-closet-search-input"
        placeholder="Search looks, designers, members"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        aria-label="Search the community"
      />
      {searchInput && (
        <button
          type="button"
          className="shared-closet-search-clear"
          onClick={() => setSearchInput('')}
          aria-label="Clear search"
        >
          &times;
        </button>
      )}
    </div>
  );

  // --- Loading state (initial load) ---
  if (isLoading) {
    return (
      <div className="shared-closet-browser">
        {searchBar}
        <div className="shared-closet-grid-wrapper">
          <div className="shared-closet-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // --- Error state ---
  if (isError && !items.length) {
    return (
      <div className="shared-closet-browser">
        {searchBar}
        <div className="shared-closet-error">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#e0a800" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span className="shared-closet-error-text">
            {error?.data?.message || 'Could not load the community. Please try again later.'}
          </span>
          <button type="button" onClick={refetch}>Retry</button>
        </div>
      </div>
    );
  }

  // --- Empty state: no match for a search is a different problem from an
  // empty community, and telling them apart is the difference between "try
  // another word" and "come back later". ---
  if (!items.length) {
    return (
      <div className="shared-closet-browser">
        {searchBar}
        <div className="shared-closet-empty">
          {query ? (
            <>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <span className="shared-closet-empty-title">No looks match “{query}”</span>
              <span className="shared-closet-empty-text">
                Try a designer’s name, a member, or a word from the piece.
              </span>
            </>
          ) : (
            <>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
                <polyline points="16,6 12,2 8,6" />
                <line x1="12" y1="2" x2="12" y2="15" />
              </svg>
              <span className="shared-closet-empty-title">Nothing here yet</span>
              <span className="shared-closet-empty-text">
                Designer pieces and shared looks will show up here.
              </span>
            </>
          )}
        </div>
      </div>
    );
  }

  // Render an item card
  const renderItemCard = (item) => (
    <div
      key={item.id}
      className={`shared-item-card ${item.isOwn ? 'shared-item-card--own' : ''}`}
      onClick={() => handleItemClick(item)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') handleItemClick(item); }}
    >
      <div className="shared-item-image-wrapper">
        <img
          className="shared-item-image"
          src={getItemImageUrl(item)}
          alt={item.clothesName || 'Community item'}
          loading="lazy"
        />
        {item.isOwn && <span className="shared-item-own-badge">Your item</span>}
        {item.tryOnCount > 0 && (
          <span className="shared-item-tryon-badge">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            {item.tryOnCount}
          </span>
        )}
      </div>
      <div className="shared-item-info">
        {item.clothesName && (
          <span className="shared-item-name">{item.clothesName}</span>
        )}
        <span className="shared-item-owner">
          {item.isOwn ? 'You' : (
            <>by <span className="shared-item-owner-name">
              {item.brand || item.ownerUsername || item.owner?.username || 'SUSU member'}
            </span></>
          )}
        </span>
      </div>
    </div>
  );

  // --- Items grid ---
  return (
    <div className="shared-closet-browser">
      {searchBar}
      <div className="shared-closet-grid-wrapper">
        {/* Own shared items section */}
        {ownItems.length > 0 && (
          <>
            <div className="shared-section-label">Your Shared Items</div>
            <div className="shared-closet-grid">
              {ownItems.map(renderItemCard)}
            </div>
          </>
        )}

        {/* Community items section */}
        {communityItems.length > 0 && (
          <>
            {ownItems.length > 0 && (
              <div className="shared-section-label">From the Community</div>
            )}
            <div className="shared-closet-grid">
              {communityItems.map(renderItemCard)}
            </div>
          </>
        )}

        <InfiniteScrollSentinel hasMore={hasMore} loading={isFetching} error={isError}
          onLoadMore={loadMore} onRetry={refetch} count={items.length} />

        {/* End of the feed doubles as the way back, so reaching the bottom is
            never a dead end. */}
        {!hasMore && !isFetching && (
          <button type="button" className="shared-closet-end" onClick={onBack}>
            <span className="shared-closet-end-rule" />
            <span className="shared-closet-end-text">
              &lsaquo; {query ? 'end of results' : 'no more looks'}, back to my closet
            </span>
            <span className="shared-closet-end-rule" />
          </button>
        )}
      </div>
    </div>
  );
};

export default SharedClosetBrowser;
